import { flow, types } from 'mobx-state-tree';
import { OrganizationBillingDetailDtoModelType } from './models';
import { SmsAddonFailureReason, SmsAddonFailureReasonEnumType } from './models/SmsAddonFailureReasonEnum';
import BillingPlanQuery from './queries/billing/BillingPlanQuery';
import getAppRoot from './utils/getAppRoot';
import { DateTime } from 'luxon';

const UsageLimitModel = types.model({
  visible: types.boolean,
  newOrdersCount: types.maybe(types.number),
  usageLimit: types.maybe(types.number),
  currentPeriodUsage: types.maybe(types.number),
});

const Billing = types.model('Billing')
  .props({
    usageLimitExceeded: types.optional(UsageLimitModel, { visible: false }),
    isNewPlanLoading: types.optional(types.boolean, false),
    subscribeStatus: types.optional(
      types.enumeration('SmsSubscribeStatus', ['idle', 'submitting', 'success', 'error']),
      'idle',
    ),
    pendingSubscribeRequestUuid: types.maybeNull(types.string),
    subscribeFailureReason: types.maybeNull(SmsAddonFailureReasonEnumType),
  })
  .actions((self) => ({
    setShowUsageLimitExceeded(visible: boolean, newOrdersCount?: number, usageLimit?: number | null, currentPeriodUsage?: number) {
      self.usageLimitExceeded = { visible, newOrdersCount, usageLimit: usageLimit == null ? undefined : usageLimit, currentPeriodUsage };
    },
    setNewPlanLoading(loading: boolean) {
      self.isNewPlanLoading = loading;
    },
    getBillingDetails() {
      return getAppRoot(self).model.queryGetBillingDetails(
        {},
        BillingPlanQuery,
        { fetchPolicy: 'no-cache' },
      ).currentPromise();
    },
    createCheckoutSession({ includeSmsAddon = false }: { includeSmsAddon?: boolean } = {}) {
      return getAppRoot(self).model.mutateCreateCheckoutSession(
        { includeSmsAddon },
        (s) => s.clientSecret,
      ).currentPromise();
    },
    getCheckoutSession(sessionId: string) {
      // Outcome of checkout
      return getAppRoot(self).model.queryGetCheckoutSession(
        { sessionId },
        (s) => s.status,
      ).currentPromise();
    },
    subscribeSmsAddon: flow(function* () {
      self.subscribeStatus = 'submitting';
      self.pendingSubscribeRequestUuid = null;
      self.subscribeFailureReason = null;
      const result = yield getAppRoot(self).model.mutateSubscribeSmsAddon().currentPromise();
      const requestUuid = result.subscribeSmsAddon.requestUuid ?? null;
      if (!requestUuid) {
        throw new Error('subscribeSmsAddon: no requestUuid returned');
      }
      self.pendingSubscribeRequestUuid = requestUuid;
    }),
    onSmsAddonSubscribed(requestUuid: string) {
      if (requestUuid !== self.pendingSubscribeRequestUuid) return;
      self.pendingSubscribeRequestUuid = null;
      self.subscribeStatus = 'success';
      const root = getAppRoot(self);
      root.billing.getBillingDetails();
      root.customerNotification.getCNSettings();
    },
    onSmsAddonFailed(requestUuid: string, reason: SmsAddonFailureReason | null) {
      if (requestUuid !== self.pendingSubscribeRequestUuid) return;
      self.pendingSubscribeRequestUuid = null;
      self.subscribeStatus = 'error';
      self.subscribeFailureReason = reason;
    },
    dismissSubscribeFeedback() {
      self.subscribeStatus = 'idle';
      self.subscribeFailureReason = null;
      self.pendingSubscribeRequestUuid = null;
    },
  }))
  .views((self) => ({
    get billingPlan(): OrganizationBillingDetailDtoModelType | undefined {
      // We've been referencing everywhere else using the workspace's org id, but we don't have that context here
      const activeSupportSession = getAppRoot(self).auth?.supportSession;

      const organizationUuid = activeSupportSession
        ? activeSupportSession.organization.uuid
        : getAppRoot(self).account.user?.organization?.uuid;

      return organizationUuid ? getAppRoot(self).model.organizationBillingDetailDtos.get(organizationUuid) : undefined;
    },
  })).views((self) => ({
    get hasSmsAddon(): boolean {
      const allowSmsOverride = getAppRoot(self).customerNotification?.cnSettings?.allowSms ?? false;
      return allowSmsOverride || (self.billingPlan?.smsAddonActive ?? false);
    },
  })).views((self) => ({
    get isGrowthPlan(): boolean {
      return self.billingPlan?.isGrowthPlan ?? false;
    },
    get trialDaysLeft(): number | null {
      if (self.billingPlan?.isGrowthTrialPlan) {
        if (self.billingPlan?.currentPeriodEnd) {
          const endDate = DateTime.fromISO(self.billingPlan.currentPeriodEnd);
          const daysLeft = Math.ceil(endDate.diffNow('days').days);
          return daysLeft > 0 ? daysLeft : 0;
        }
      }
      return null;
    },
  }),
  );
export default Billing;
